<template>
  <div class="relative text-yapgray-500">
    <label
      v-if="!noLabel && !floating"
      :for="id ? id : name"
      class="left-2 z-20 text-xs text-yapgray-600 dark:text-yapgray-400"
    >
      {{ label ? label : schema.title }}
    </label>
    <div class="relative flex w-full items-center">
      <VueDatePicker
        :id="id ? id : name"
        :name="name"
        v-model="modelValue"
        :dark="state.isDarkTheme"
        :placeholder="placeholder"
        :enable-time-picker="enableTimePicker"
        :disabled-week-days="state.disabledWeekDays"
        :disabled-dates="state.disabledBankingDates"
        :locale="getPreferredLanguage()"
        :format-locale="state.preferredLanguage"
        :format="displayFormat"
        :model-type="modelType"
        timezone="Europe/Zurich"
        :teleport="true"
        :min-date="minDate"
        :readonly="readonly"
        :cancel-text="t('dict.label.cancel')"
        :select-text="t('dict.label.select')"
        :utc="utc"
        :clearable="clearable"
        :auto-apply="autoApply"
        :loading="state.paymentDatesLoading"
        :style="{ '--dp-input-padding': floating ? '14px 60px 14px 24px' : '6px 30px 6px 12px' }"
      >
        <template #input-icon>
          <YapIcon name="calendar-outline" class="input-slot-image ml-3 h-5 w-5" />
        </template>
        <template #dp-input="{ value, onInput, onEnter, onTab, onClear, onBlur, onKeypress, onPaste, isMenuOpen }">
          <input
            type="text"
            :value="value"
            class="peer w-full overflow-visible border-yapgray-300 pl-10 text-yapgray-800 focus:z-10 focus:border-primary-500 focus:ring-primary-500 focus:dark:ring-primary-dark-500 dark:border-yapdarkgray-600 dark:bg-yapdarkgray-800 dark:text-yapgray-100"
            :class="{
              'bg-yapgray-100 text-yapgray-600 dark:bg-yapdarkgray-900 dark:text-yapgray-200':
                readonly || schema.readonly,
              'rounded-lg shadow-sm': !grouped,
              'border-0': grouped,
              'h-14': floating,
              'border-yapred-500 focus:border-yapred-500 focus:ring-yapred-500 dark:border-yapred-500 dark:focus:border-yapred-500 dark:focus:ring-yapred-500':
                error,
              'pt-3.5 placeholder-transparent dark:placeholder-transparent': floating,
              'dark:placeholder-yapdarkgray-300': !floating
            }"
            v-bind="$attrs"
            data-testid="datepicker-input"
          />
          <label
            v-if="!noLabel && floating"
            :for="id ? id : name"
            class="absolute left-2 top-0.5 z-20 w-full text-xs text-yapgray-500 transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-base peer-placeholder-shown:text-yapgray-400 peer-focus:top-0.5 peer-focus:text-xs peer-focus:text-primary-500 peer-focus:dark:text-primary-dark-500"
            :class="{
              'text-yapgray-600 dark:text-yapgray-200': readonly || schema.readonly
            }"
          >
            <span
              class="w-full pb-1 pr-4"
              :class="{
                'bg-yapgray-200 text-yapgray-600 dark:bg-yapdarkgray-700 dark:text-yapgray-200': schema.readonly,
                'bg-white/80 dark:bg-yapdarkgray-800/80': !readonly
              }"
              >{{ label ? label : schema.title }}</span
            >
          </label>
        </template>
      </VueDatePicker>
    </div>
  </div>
</template>

<script setup lang="ts">
import { format, parseISO } from 'date-fns';
import '@vuepic/vue-datepicker/dist/main.css';
import VueDatePicker from '@vuepic/vue-datepicker';
// TODO: figure out how to dynamically load the different locale based on current language...
import { enGB, de, frCH, itCH } from 'date-fns/locale';
import { computed, onMounted, reactive, watch } from 'vue';

import apiGw from '@/services/apiGw';
import { Theme } from '@/types/general';
import { useI18n } from '@/plugins/i18n';
import YapIcon from '@/components/YapIcon.vue';
import { getPreferredLanguage } from '@/utils/locale';
import { useProfileStore } from '@/store/profileStore';
import { SchemaItem } from '@/components/form/types/schemaItem';
import useInputValidation from '@/composables/useInputValidation';
import { YapInputValidationError } from '@/components/form/types/yapInputValidationError';

const modelValue = defineModel<string>();
const emit = defineEmits(['update:modelValue']);

const props = withDefaults(
  defineProps<{
    schema: SchemaItem;
    name: string;
    id?: string;
    label?: string;
    noLabel?: boolean;
    minDate?: string;
    placeholder?: string;
    floating?: boolean;
    enableTimePicker?: boolean;
    restrictBankingDates?: boolean;
    grouped?: boolean;
    errors?: Array<YapInputValidationError>;
    isEbill?: boolean;
    dueDate?: string;
    modelType?: string;
    shouldApplyExpectedExecutionDate?: boolean;
    weekendAllowed?: boolean;
    readonly?: boolean;
    utc?: boolean;
    clearable?: boolean;
    autoApply?: boolean;
    displayFormat?: (date: any) => string;
  }>(),
  {
    id: undefined,
    label: undefined,
    noLabel: false,
    minDate: undefined,
    placeholder: undefined,
    floating: false,
    enableTimePicker: false,
    restrictBankingDates: true,
    grouped: false,
    errors: undefined,
    isEbill: false,
    dueDate: undefined,
    modelType: undefined,
    shouldApplyExpectedExecutionDate: false,
    weekendAllowed: undefined,
    utc: true,
    clearable: true,
    autoApply: true,
    displayFormat: (date: any) => format(date, 'dd.MM.yyyy')
  }
);

interface State {
  isDarkTheme: boolean;
  disabledWeekDays: Array<number>;
  disabledBankingDates: Array<Date>;
  formatLocale: any;
  localError?: YapInputValidationError;
  error?: YapInputValidationError;
  preferredLanguage: any;
  paymentDatesLoading?: boolean;
}

const { t } = useI18n();
const profileStore = useProfileStore();
const { filterErrorFor } = useInputValidation();

const state: State = reactive({
  isDarkTheme: computed(() => profileStore.getTheme === Theme.DARK_THEME),
  disabledWeekDays: [],
  disabledBankingDates: [],
  formatLocale: undefined,
  localError: undefined,
  error: props.errors ? filterErrorFor(props.name, props.errors) : undefined,
  preferredLanguage: computed(() => {
    const lang = getPreferredLanguage();
    switch (lang) {
      case 'de':
        return de;
      case 'fr':
        return frCH;
      case 'it':
        return itCH;
      default:
        return enGB;
    }
  }),
  paymentDatesLoading: props.restrictBankingDates ? true : false
});

const error = computed(() => {
  if (!!state.error) {
    return state.error;
  } else if (!!state.localError) {
    return state.localError;
  }
  return undefined;
});

onMounted(() => {
  if (props.restrictBankingDates) {
    loadPaymentDates();
  }
  const lang = getPreferredLanguage();
  switch (lang) {
    case 'fr':
      state.formatLocale = frCH;
      break;
    case 'de':
      state.formatLocale = de;
      break;
    case 'it':
      state.formatLocale = itCH;
      break;
    default:
      state.formatLocale = enGB;
      break;
  }
});

watch(
  () => props.isEbill,
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      loadPaymentDates();
    }
  }
);
watch(
  () => props.dueDate,
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      loadPaymentDates();
    }
  }
);

async function loadPaymentDates() {
  try {
    state.paymentDatesLoading = true;
    const resp = await apiGw.get('/payment-dates', {
      params: {
        isEBill: `${props.isEbill}`,
        dueDate: props.dueDate && format(new Date(props.dueDate), 'yyyy-MM-dd')
      }
    });

    const clearingHoliday = resp.data.clearingHolidays;
    const recommendedExecutionDate = resp.data.recommendedExecutionDate;
    const nextExecutionDate = resp.data.nextExecutionDate;

    let weekendAllowed = resp.data.weekendAllowed;
    if (props.weekendAllowed !== undefined) {
      weekendAllowed = props.weekendAllowed;
    }

    if (!weekendAllowed) {
      state.disabledWeekDays = [6, 0];
    }
    if (clearingHoliday) {
      state.disabledBankingDates = clearingHoliday;
    }

    if (!props.shouldApplyExpectedExecutionDate) {
      let date;
      if (recommendedExecutionDate) {
        date = recommendedExecutionDate;
      } else {
        date = nextExecutionDate;
      }
      if (props.modelType) {
        date = format(parseISO(date), props.modelType);
      }
      emit('update:modelValue', date);
    }
    state.paymentDatesLoading = false;
  } catch (error) {
    console.error('Could not load payment dates...');
    state.paymentDatesLoading = false;
  }
}
</script>

<style>
@import '@vuepic/vue-datepicker/dist/main.css';

.dp__theme_light {
  --dp-background-color: #ffffff;
  --dp-text-color: #212121;
  --dp-hover-color: #f3f3f3;
  --dp-hover-text-color: #212121;
  --dp-hover-icon-color: #959595;
  --dp-primary-color: #2527c8; /** primary-500 */
  --dp-primary-text-color: #f8f5f5;
  --dp-secondary-color: #c0c4cc;
  --dp-border-color: #ddd;
  --dp-menu-border-color: #ddd;
  --dp-border-color-hover: #aaaeb7;
  --dp-disabled-color: #f6f6f6;
  --dp-scroll-bar-background: #f3f3f3;
  --dp-scroll-bar-color: #959595;
  --dp-success-color: #76d275;
  --dp-success-color-disabled: #a3d9b1;
  --dp-icon-color: #959595;
  --dp-danger-color: #ff6f60;
  --dp-highlight-color: rgba(25, 118, 210, 0.1);
}

.dp__theme_dark {
  --dp-background-color: #1e293b; /** slate-800 */
  --dp-text-color: #ffffff;
  --dp-hover-color: #484848;
  --dp-hover-text-color: #ffffff;
  --dp-hover-icon-color: #959595;
  --dp-primary-color: #5ab0f4; /** primary-dark-500 */
  --dp-primary-text-color: #ffffff;
  --dp-secondary-color: #a9a9a9;
  --dp-border-color: #475569; /** slate-600 */
  --dp-menu-border-color: #475569; /** slate-600 */
  --dp-border-color-hover: #aaaeb7;
  --dp-disabled-color: #737373;
  --dp-scroll-bar-background: #212121;
  --dp-scroll-bar-color: #484848;
  --dp-success-color: #00701a;
  --dp-success-color-disabled: #428f59;
  --dp-icon-color: #959595;
  --dp-danger-color: #e53935;
  --dp-highlight-color: rgba(0, 92, 178, 0.2);
}

:root {
  --dp-border-radius: 8px;
}
</style>
